/**
 * Carousel scroll arithmetic, kept out of the component so it can be tested:
 * jsdom reports every layout measurement as 0, so a test that rendered the
 * arrows would always see them hidden and prove nothing.
 *
 * Both rules are the designer prototype's
 * (`prototypes/purchase-flow/purchase-flow-catalog-v2.html`, `syncArrows` and
 * its click handler).
 */

/** The 1px slack the prototype allows, because browsers report fractional
 *  scroll positions and an exact comparison leaves an arrow live at the end. */
const TOLERANCE = 1

interface ScrollMetrics {
  scrollLeft: number
  scrollWidth: number
  clientWidth: number
}

export interface CarouselScrollState {
  canScrollPrev: boolean
  canScrollNext: boolean
}

/**
 * Which directions the row can still travel. An arrow exists exactly when there
 * is something that way — which is also what drives the edge veil, so the two
 * cannot disagree about whether anything is cut off.
 */
export const carouselScrollState = (
  { scrollLeft, scrollWidth, clientWidth }: ScrollMetrics
): CarouselScrollState => {
  const max = scrollWidth - clientWidth
  const scrollable = max > TOLERANCE
  return {
    canScrollPrev: scrollable && scrollLeft > TOLERANCE,
    canScrollNext: scrollable && scrollLeft < max - TOLERANCE,
  }
}

interface StepMetrics {
  clientWidth: number
  /**
   * Distance from one card's leading edge to the next one's — the card plus the
   * space between them.
   *
   * MEASURED, never derived as `cardWidth + columnGap`. Each card carries a
   * negative margin equal to half the gap so its hover tint has room inside the
   * scroller's clip box, which cancels the gap exactly: the row computes a
   * `column-gap` of 24px and a `margin-left` of -12px, so the true pitch is the
   * card's own 258px, not the 282px the two properties suggest.
   */
  pitch: number
}

/**
 * How far one arrow click travels: as many WHOLE cards as fit, never a raw
 * viewport width, which would leave a card straddling the edge for scroll-snap
 * to correct afterwards. At least one card always moves, so a viewport narrower
 * than a single card still advances.
 */
export const carouselScrollStep = ({ clientWidth, pitch }: StepMetrics): number => {
  if (pitch <= 0) return clientWidth
  return Math.max(1, Math.floor(clientWidth / pitch)) * pitch
}
